import React, { useEffect, useState, useRef } from "react";

/**
 * Animated typewriter that supports highlighted segments.
 * Each segment: { text: string, highlight?: boolean }
 * Highlighted segments render with the accent color (Frente Ya yellow).
 */
export const Typewriter = ({
  segments,
  speed = 45,
  startDelay = 200,
  className = "",
  cursorClassName = "text-[#F5B400]",
  highlightClassName = "text-[#F5B400]",
  onDone,
  testid,
}) => {
  // Build full string + per-char highlight flag
  const flat = [];
  segments.forEach((s) => {
    for (const ch of s.text) flat.push({ ch, hl: !!s.highlight });
  });

  const [i, setI] = useState(0);
  const [blink, setBlink] = useState(true);
  const finishedRef = useRef(false);

  useEffect(() => {
    let timer;
    if (i === 0) {
      timer = setTimeout(() => setI(1), startDelay);
    } else if (i < flat.length) {
      // Slow down a bit on spaces and punctuation for a more human cadence
      const prevCh = flat[i - 1]?.ch || "";
      const extra = /[.,;]/.test(prevCh) ? 220 : prevCh === " " ? 20 : 0;
      timer = setTimeout(() => setI(i + 1), speed + extra);
    } else if (!finishedRef.current) {
      finishedRef.current = true;
      onDone && onDone();
    }
    return () => clearTimeout(timer);
  }, [i, flat.length, speed, startDelay, onDone]);

  // Cursor blink
  useEffect(() => {
    const t = setInterval(() => setBlink((b) => !b), 480);
    return () => clearInterval(t);
  }, []);

  // Render visible chars
  const visible = flat.slice(0, i);
  const done = i >= flat.length;

  // Group consecutive same-highlight chars to render fewer spans
  const rendered = [];
  let buf = "";
  let bufHl = false;
  visible.forEach((c, idx) => {
    if (idx === 0) {
      buf = c.ch;
      bufHl = c.hl;
    } else if (c.hl === bufHl) {
      buf += c.ch;
    } else {
      rendered.push({ text: buf, hl: bufHl });
      buf = c.ch;
      bufHl = c.hl;
    }
  });
  if (buf) rendered.push({ text: buf, hl: bufHl });

  return (
    <span className={className} data-testid={testid} aria-label={segments.map((s) => s.text).join("")}>
      {rendered.map((p, idx) =>
        p.hl ? (
          <span key={idx} className={highlightClassName}>
            {p.text}
          </span>
        ) : (
          <span key={idx}>{p.text}</span>
        )
      )}
      <span
        aria-hidden
        className={`inline-block ml-1 ${cursorClassName} transition-opacity ${
          blink || !done ? "opacity-100" : "opacity-0"
        }`}
        style={{ transform: "translateY(-0.05em)" }}
      >
        |
      </span>
    </span>
  );
};

export default Typewriter;
